import { Trophy, ScrollText, Star, TrendingUp } from "lucide-react";
import { PageHero, Reveal, SectionHead, Counter, EmptyState, Tag, BannerStrip } from "@/components/Shared";
import { useCollection, fmtDateShort, IMG } from "@/lib/api";

const GROUPS = [
  { kind: "award", icon: Trophy, title: "Awards", note: "Honours won by the club and its members." },
  { kind: "citation", icon: ScrollText, title: "Citations", note: "District and Rotary citations earned through the year." },
  { kind: "recognition", icon: Star, title: "Recognitions", note: "Moments where our work was noticed and celebrated." },
  { kind: "milestone", icon: TrendingUp, title: "Milestones", note: "The numbers that mark our growth." },
];

export default function Achievements() {
  const { items, loading } = useCollection("achievements");

  return (
    <main>
      <PageHero eyebrow="Our Achievements" title="Proof of" italic="the fire" image={IMG.emberTexture}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          Awards, citations, recognitions and milestones — every one of them earned by members who showed up.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="achievements-section">
        {loading && <p className="text-sm text-silver-muted">Loading achievements…</p>}
        {GROUPS.map((g) => {
          const group = (items || []).filter((a) => a.kind === g.kind);
          if (group.length === 0) return null;
          return (
            <div key={g.kind} className="mb-20" data-testid={`achievements-${g.kind}`}>
              <SectionHead eyebrow={g.title} title={g.note.split(" ").slice(0, -1).join(" ")} italic={g.note.split(" ").slice(-1)} />
              <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {group.map((a, i) => (
                  <Reveal key={a.id} delay={(i % 3) * 0.08} className="h-full">
                    <div className="card-line group flex h-full flex-col p-7" data-testid={`achievement-card-${a.id}`}>
                      <div className="flex items-center justify-between">
                        <g.icon size={22} strokeWidth={1.4} className="text-ember" />
                        <Tag>{fmtDateShort(a.date)}</Tag>
                      </div>
                      <h3 className="mt-6 font-display text-2xl font-semibold text-silver">{a.title}</h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-silver-muted">{a.detail}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          );
        })}
        {!loading && (items || []).length === 0 && (
          <EmptyState title="The trophy shelf is warming up" note="Achievements will be added as the year unfolds." />
        )}
      </section>

      <BannerStrip image={IMG.celestialSky} height="py-24">
        <div className="mx-auto max-w-4xl px-6 text-center" data-testid="milestone-band">
          <p className="eyebrow">Signature Milestone · 2026–27</p>
          <p className="mt-6 font-display text-7xl font-semibold metallic sm:text-8xl">
            <Counter value={150} suffix="+" />
          </p>
          <p className="mt-4 font-display text-2xl italic text-silver-dim">members strong — and still growing</p>
        </div>
      </BannerStrip>
    </main>
  );
}
